// Envio do expediente para assinatura digital.
//
// O modal em si é compartilhado desde 21/08/2026
// (`components/assinatura/ModalAssinaturaDigital.tsx`) — as vendas usam o
// mesmo. Aqui fica só o que é do expediente: QUEM já vem pré-selecionado.
//
// Esta escolha do pré-preenchimento é regra de negócio, não conveniência:
//
//  • Um passo só: quem foi marcado como "Assina" na aba Quem participa já
//    entra na lista — a queixa real era declarar os signatários no editor e
//    ter que convidar as MESMAS pessoas de novo aqui. A lista continua
//    editável: dá pra tirar, somar e preencher o "Assina como" antes de enviar.
//
//  • Reenvio (envio anterior falhou ou foi cancelado): a fonte é o ENVELOPE
//    anterior, não os envolvidos — é lá que mora a qualificação digitada.
//    Pré-preencher dos envolvidos faria a lista voltar "pronta" com o
//    "Assina como" silenciosamente vazio, e assinatura não se refaz.

import { useEffect, useMemo, useState } from 'react';
import ModalAssinaturaDigital from '../../components/assinatura/ModalAssinaturaDigital';
import { SignatarioEnvio, Envelope } from '../../components/assinatura/tipos';
import { enviarParaAssinatura, Envolvido } from './api';
import { sugerirSignatariosContrato } from '../juridico-contratos/api';

export default function ModalAssinatura({ open, expedienteId, codigo, envolvidos, envelopeAnterior, area, onClose, onEnviado }: {
  open: boolean;
  expedienteId: string | null;
  codigo: string | null;
  envolvidos?: Envolvido[];
  envelopeAnterior?: Envelope | null;
  /** Quando 'juridico', a pré-seleção segue a da VENDA: as PARTES do contrato
   *  (pela ficha), não os envolvidos internos. Pedido do Lucas, 31/08. */
  area?: string;
  onClose: () => void;
  onEnviado: () => void;
}) {
  // Sugestão do contrato: quem assina pela Hydrodata + pela outra parte,
  // qualificados ("Contratada"/"Contratante") conforme o papel da ficha.
  const [sugestaoJur, setSugestaoJur] = useState<SignatarioEnvio[] | null>(null);
  const [avisosJur, setAvisosJur] = useState<string[]>([]);
  // Papéis que o SERVIDOR vai exigir neste contrato (16/09/2026): vêm da ficha
  // (Contratante/Contratada, Mutuante/Mutuária…), nunca de texto fixo aqui. Sem
  // ficha ou em ofício, a lista fica vazia — a janela não trava o que o servidor
  // aceita.
  const [papeisJur, setPapeisJur] = useState<string[]>([]);
  useEffect(() => {
    if (!open || area !== 'juridico' || !expedienteId) { setSugestaoJur(null); setAvisosJur([]); setPapeisJur([]); return; }
    (async () => {
      try {
        const r = await sugerirSignatariosContrato(expedienteId);
        setSugestaoJur(r.signatarios || []);
        setAvisosJur(r.avisos || []);
        setPapeisJur(Array.isArray(r.papeis_obrigatorios) ? r.papeis_obrigatorios : []);
      } catch { setSugestaoJur(null); setAvisosJur([]); setPapeisJur([]); }
    })();
  }, [open, area, expedienteId]);
  const preSelecionados = useMemo<SignatarioEnvio[]>(() => {
    const doEnvelope = envelopeAnterior
      && ['erro_envio', 'cancelado'].includes(envelopeAnterior.status)
      && envelopeAnterior.signatarios?.length
      ? envelopeAnterior.signatarios.map((s, i) => ({
          nome: s.nome, email: s.email,
          origem: (s.origem === 'externo' ? 'externo' : 'interno') as 'interno' | 'externo',
          usuario_email: s.origem === 'externo' ? undefined : (s.usuario_email || s.email),
          ordem: i + 1, d4sign_act: '1' as const,
          qualificacao: s.qualificacao || undefined,
        }))
      : null;
    if (doEnvelope) return doEnvelope;
    // CONTRATO: a lista abre com as PARTES (da ficha), como na venda. Os
    // envolvidos internos continuam existindo — para sigilo e edição — mas
    // não são quem assina um contrato por padrão.
    if (area === 'juridico' && sugestaoJur) return sugestaoJur;
    return (envolvidos || [])
      .filter(e => e.papel === 'signatario')
      .map((e, i) => ({
        nome: e.nome_exibicao || e.usuario_email.split('@')[0],
        email: e.usuario_email,
        origem: e.externo ? 'externo' as const : 'interno' as const,
        usuario_email: e.externo ? undefined : e.usuario_email,
        ordem: i + 1, d4sign_act: '1' as const,
      }));
  }, [envolvidos, envelopeAnterior, open, area, sugestaoJur]);

  return (
    <ModalAssinaturaDigital
      open={open}
      titulo={codigo || 'expediente'}
      preSelecionados={preSelecionados}
      descricaoDoAviso={`Cada pessoa recebe um link da D4 Sign para assinar. O documento enviado é o PDF definitivo do expediente — o mesmo que está no acervo.${avisosJur.length ? ` ⚠️ ${avisosJur.join(' ')}` : ''}`}
      onEnviar={(p) => enviarParaAssinatura(expedienteId!, p)}
      onClose={onClose}
      // Contrato do Jurídico: os papéis da ficha, que é o que o servidor exige.
      // Ofício (e contrato sem ficha) continua sem exigência — a janela não pode
      // travar o que o servidor aceita.
      papeisObrigatorios={papeisJur}
      onEnviado={onEnviado}
    />
  );
}
